<template>
  <span
    class="zeus-loader"
    :class="variant === 'strike' ? 'zeus-loader--strike-phase' : 'zeus-loader--scan-phase'"
    role="presentation"
    :style="rootStyle"
    aria-hidden="true"
  >
    <template v-if="variant === 'scan'">
      <span class="zeus-loader__scan-dim" />
      <span class="zeus-loader__scan-halo" />
      <span class="zeus-loader__scan-band" />
      <span class="zeus-loader__scan-spark zeus-loader__scan-spark-a" />
      <span class="zeus-loader__scan-spark zeus-loader__scan-spark-b" />
      <span class="zeus-loader__scan-spark zeus-loader__scan-spark-c" />
      <span class="zeus-loader__scan-spark zeus-loader__scan-spark-d" />
    </template>
    <template v-else>
      <span class="zeus-loader__strike-backdrop" />
      <span class="zeus-loader__strike-flicker">
        <span class="zeus-loader__strike-flicker-bar zeus-loader__strike-flicker-bar--a" />
        <span class="zeus-loader__strike-flicker-bar zeus-loader__strike-flicker-bar--b" />
      </span>
      <svg
        class="zeus-loader__strike-svg"
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid meet"
        aria-hidden="true"
      >
        <defs>
          <linearGradient
            :id="hotGradientId"
            x1="50%"
            y1="0%"
            x2="50%"
            y2="100%"
          >
            <stop offset="0%" :style="{ 'stop-color': `color-mix(in oklab, var(--color-brand-secondary) 38%, white)` }" />
            <stop offset="35%" stop-color="#ffffff" />
            <stop offset="100%" :style="{ 'stop-color': `var(--color-brand-primary)` }" />
          </linearGradient>
          <linearGradient :id="coolGradientId" x1="0%" y1="0%" x2="100%" y2="82%">
            <stop offset="0%" :style="{ 'stop-color': `color-mix(in oklab, var(--color-brand-secondary) 72%, white)` }" />
            <stop offset="45%" :style="{ 'stop-color': `var(--color-brand-secondary)` }" />
            <stop offset="100%" :style="{ 'stop-color': `color-mix(in oklab, var(--color-brand-primary) 92%, black)` }" />
          </linearGradient>
        </defs>
        <g class="zeus-loader__strike-bolt">
          <path
            class="zeus-loader__strike-ant zeus-loader__strike-ant--r"
            pathLength="100"
            fill="none"
            stroke="var(--color-brand-secondary)"
            stroke-opacity="0.78"
            stroke-width="4.25"
            stroke-linecap="square"
            stroke-linejoin="miter"
            d="M 50 21 L 59 27"
          />
          <path
            class="zeus-loader__strike-ant zeus-loader__strike-ant--l"
            pathLength="100"
            fill="none"
            stroke="var(--color-brand-secondary)"
            stroke-opacity="0.78"
            stroke-width="4.25"
            stroke-linecap="square"
            stroke-linejoin="miter"
            d="M 50 21 L 41 27"
          />
          <path
            class="zeus-loader__strike-main zeus-loader__strike-main--glow"
            pathLength="100"
            fill="none"
            :stroke="`url(#${coolGradientId})`"
            stroke-width="10"
            stroke-linecap="square"
            stroke-linejoin="miter"
            opacity="0.55"
            d="M 50 18 L 45 29 L 54 39 L 45 47 L 54 54 L 47 61 L 53 70 L 47 75 L 50 82"
          />
          <path
            class="zeus-loader__strike-main zeus-loader__strike-main--mid"
            pathLength="100"
            fill="none"
            :stroke="`url(#${hotGradientId})`"
            stroke-width="4"
            stroke-linecap="square"
            stroke-linejoin="miter"
            d="M 50 18 L 45 29 L 54 39 L 45 47 L 54 54 L 47 61 L 53 70 L 47 75 L 50 82"
          />
          <path
            class="zeus-loader__strike-main zeus-loader__strike-main--core"
            pathLength="100"
            fill="none"
            stroke="#ffffff"
            stroke-width="1.8"
            stroke-linecap="square"
            stroke-linejoin="miter"
            d="M 50 18 L 45 29 L 54 39 L 45 47 L 54 54 L 47 61 L 53 70 L 47 75 L 50 82"
          />
          <path
            class="zeus-loader__strike-chain zeus-loader__strike-chain--d1"
            pathLength="100"
            fill="none"
            stroke="var(--color-brand-secondary)"
            stroke-width="2"
            stroke-linecap="square"
            stroke-linejoin="miter"
            opacity="0.72"
            d="M 45 31 L 39 34 L 36 40 L 32 46"
          />
          <path
            class="zeus-loader__strike-chain zeus-loader__strike-chain--d2"
            pathLength="100"
            fill="none"
            stroke="var(--color-brand-primary)"
            stroke-width="2"
            stroke-linecap="square"
            stroke-linejoin="miter"
            opacity="0.68"
            d="M 54 41 L 62 46 L 65 53 L 70 58"
          />
          <path
            class="zeus-loader__strike-chain zeus-loader__strike-chain--d3"
            pathLength="100"
            fill="none"
            stroke="var(--color-base-white)"
            stroke-width="1.35"
            stroke-linecap="square"
            stroke-linejoin="miter"
            opacity="0.58"
            d="M 44 43 L 55 46"
          />
          <path
            class="zeus-loader__strike-chain zeus-loader__strike-chain--d4"
            pathLength="100"
            fill="none"
            stroke="var(--color-brand-secondary)"
            stroke-width="1.75"
            stroke-linecap="square"
            stroke-linejoin="miter"
            opacity="0.65"
            d="M 45 48 L 40 53 L 38 60"
          />
          <path
            class="zeus-loader__strike-chain zeus-loader__strike-chain--d5"
            pathLength="100"
            fill="none"
            stroke="var(--color-brand-primary)"
            stroke-width="1.75"
            stroke-linecap="square"
            stroke-linejoin="miter"
            opacity="0.62"
            d="M 54 55 L 60 61 L 64 69"
          />
          <path
            class="zeus-loader__strike-chain zeus-loader__strike-chain--d6"
            pathLength="100"
            fill="none"
            stroke="var(--color-base-white)"
            stroke-width="1.25"
            stroke-linecap="square"
            stroke-linejoin="miter"
            opacity="0.52"
            d="M 46 62 L 55 63"
          />
          <path
            class="zeus-loader__strike-chain zeus-loader__strike-chain--d7"
            pathLength="100"
            fill="none"
            stroke="var(--color-brand-secondary)"
            stroke-width="1.6"
            stroke-linecap="square"
            stroke-linejoin="miter"
            opacity="0.7"
            d="M 48 74 L 42 78 L 40 84"
          />
          <path
            class="zeus-loader__strike-chain zeus-loader__strike-chain--d8"
            pathLength="100"
            fill="none"
            stroke="var(--color-brand-primary)"
            stroke-width="1.6"
            stroke-linecap="square"
            stroke-linejoin="miter"
            opacity="0.7"
            d="M 52 74 L 59 78 L 62 84"
          />
        </g>
      </svg>
      <span class="zeus-loader__strike-impact" />
    </template>
  </span>
</template>

<script setup lang="ts">
import { computed } from "vue";

const props = withDefaults(
  defineProps<{
    /** Сканирующее «волновое» оверлее или финальный удар молнии */
    variant: "scan" | "strike";
    /** Уникальный id для defs внутри SVG (несколько инстансов на экране) */
    graphicId: string;
    /** Смещение фазы анимации для нескольких плиток в ряду */
    staggerIndex?: number;
  }>(),
  { staggerIndex: 0 }
);

const strikeStaggerSlots = computed(() => Math.min(props.staggerIndex, 5));

const rootStyle = computed(() => ({
  "--zeus-scan-delay": `${props.staggerIndex * 0.12}s`,
  "--zeus-strike-delay": `${strikeStaggerSlots.value * 0.036}s`,
}));

const hotGradientId = computed(() => `zeus-loader-hot-${props.graphicId}`);
const coolGradientId = computed(() => `zeus-loader-cool-${props.graphicId}`);
</script>

<style scoped lang="scss">
/* Root: заполняет position:relative родителя (например плитка превью) */
.zeus-loader {
  position: absolute;
  inset: 0;
  z-index: 1;
  overflow: hidden;
  border-radius: inherit;
  pointer-events: none;
}

.zeus-loader--strike-phase {
  isolation: isolate;
  animation: zeus-loader-strike-container 660ms ease-out forwards;
}

/* ——— strike ——— */
.zeus-loader__strike-backdrop {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: linear-gradient(
    180deg,
    color-mix(in oklab, var(--color-base-black) 94%, transparent) 0%,
    color-mix(in oklab, var(--color-brand-primary) 18%, transparent) 52%,
    color-mix(in oklab, var(--color-brand-secondary) 32%, transparent) 100%
  );
  opacity: 0.88;
}

.zeus-loader__strike-flicker {
  pointer-events: none;
  position: absolute;
  top: 9%;
  left: 50%;
  display: flex;
  width: 58%;
  height: 4px;
  translate: -50% 0;
  justify-content: space-between;
}

.zeus-loader__strike-flicker-bar {
  width: 32%;
  height: 100%;
  border-radius: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    color-mix(in oklab, var(--color-base-white) 92%, var(--color-brand-secondary)) 44%,
    color-mix(in oklab, var(--color-brand-primary) 58%, white) 70%,
    transparent
  );
  box-shadow: 0 0 9px color-mix(in oklab, var(--color-brand-primary) 55%, transparent);
  opacity: 0;
  animation: zeus-loader-strike-flicker-pop 660ms linear forwards;
}

.zeus-loader__strike-flicker-bar--a {
  animation-delay: var(--zeus-strike-delay, 0s);
}

.zeus-loader__strike-flicker-bar--b {
  animation-delay: calc(var(--zeus-strike-delay, 0s) + 0.04s);
}

.zeus-loader__strike-svg {
  position: absolute;
  left: 9%;
  right: 9%;
  top: 9%;
  bottom: 11%;
  width: auto;
  height: auto;
  overflow: visible;
  filter:
    drop-shadow(0 0 2px color-mix(in oklab, var(--color-brand-secondary) 55%, white))
    drop-shadow(0 0 6px color-mix(in oklab, var(--color-brand-primary) 48%, transparent))
    drop-shadow(0 0 12px color-mix(in oklab, var(--color-brand-secondary) 38%, transparent));
}

.zeus-loader__strike-bolt {
  transform-origin: 50% 86%;
  animation: zeus-loader-strike-bolt-release 660ms cubic-bezier(0.22, 0.92, 0.35, 1) forwards;
}

.zeus-loader__strike-ant {
  stroke-dasharray: 100;
  stroke-dashoffset: 100;
  animation: zeus-loader-strike-draw-ant 92ms cubic-bezier(0.4, 0, 0.6, 1) forwards;
}

.zeus-loader__strike-ant--r {
  animation-delay: calc(var(--zeus-strike-delay, 0s) + 0.02s);
}

.zeus-loader__strike-ant--l {
  animation-delay: calc(var(--zeus-strike-delay, 0s) + 0.068s);
}

.zeus-loader__strike-main {
  stroke-dasharray: 100;
  stroke-dashoffset: 100;
  animation: zeus-loader-strike-draw-bolt 240ms cubic-bezier(0.1, 0.72, 0.16, 1) forwards;
}

.zeus-loader__strike-main--glow {
  animation-delay: calc(var(--zeus-strike-delay, 0s) + 0.086s);
  animation-duration: 252ms;
}

.zeus-loader__strike-main--mid {
  animation-delay: calc(var(--zeus-strike-delay, 0s) + 0.09s);
  animation-duration: 236ms;
}

.zeus-loader__strike-main--core {
  animation-delay: calc(var(--zeus-strike-delay, 0s) + 0.102s);
  animation-duration: 220ms;
}

.zeus-loader__strike-chain {
  stroke-dasharray: 100;
  stroke-dashoffset: 100;
  animation: zeus-loader-strike-draw-bolt forwards;
  animation-timing-function: cubic-bezier(0.06, 0.78, 0.18, 1);
  vector-effect: non-scaling-stroke;
}

.zeus-loader__strike-chain--d1 {
  animation-duration: 186ms;
  animation-delay: calc(var(--zeus-strike-delay, 0s) + 0.098s);
}

.zeus-loader__strike-chain--d2 {
  animation-duration: 198ms;
  animation-delay: calc(var(--zeus-strike-delay, 0s) + 0.114s);
}

.zeus-loader__strike-chain--d3 {
  animation-duration: 132ms;
  animation-delay: calc(var(--zeus-strike-delay, 0s) + 0.134s);
}

.zeus-loader__strike-chain--d4 {
  animation-duration: 176ms;
  animation-delay: calc(var(--zeus-strike-delay, 0s) + 0.126s);
}

.zeus-loader__strike-chain--d5 {
  animation-duration: 188ms;
  animation-delay: calc(var(--zeus-strike-delay, 0s) + 0.138s);
}

.zeus-loader__strike-chain--d6 {
  animation-duration: 158ms;
  animation-delay: calc(var(--zeus-strike-delay, 0s) + 0.152s);
}

.zeus-loader__strike-chain--d7 {
  animation-duration: 168ms;
  animation-delay: calc(var(--zeus-strike-delay, 0s) + 0.164s);
}

.zeus-loader__strike-chain--d8 {
  animation-duration: 168ms;
  animation-delay: calc(var(--zeus-strike-delay, 0s) + 0.174s);
}

.zeus-loader__strike-impact {
  position: absolute;
  bottom: 10%;
  left: 50%;
  width: 69%;
  height: 38%;
  border-radius: 50%;
  background: radial-gradient(
    circle at 50% 38%,
    #ffffff 0%,
    color-mix(in oklab, var(--color-brand-secondary) 48%, transparent) 24%,
    color-mix(in oklab, var(--color-brand-primary) 42%, transparent) 50%,
    transparent 70%
  );
  mix-blend-mode: screen;
  opacity: 0;
  transform: translate(-50%, 38%) scale(0.32);
  animation: zeus-loader-strike-impact-burst 660ms ease-out forwards;
  animation-delay: calc(var(--zeus-strike-delay, 0s) + 0.278s);
}

@keyframes zeus-loader-strike-container {
  0%,
  88% {
    opacity: 1;
  }

  100% {
    opacity: 0;
  }
}

@keyframes zeus-loader-strike-flicker-pop {
  0%,
  5% {
    opacity: 0;
    transform: scaleX(0.65);
  }

  11% {
    opacity: 1;
    transform: scaleX(1);
  }

  16% {
    opacity: 0;
    transform: scaleX(1);
  }

  22% {
    opacity: 0.92;
    transform: scaleX(0.94);
  }

  29% {
    opacity: 0;
  }

  100% {
    opacity: 0;
  }
}

@keyframes zeus-loader-strike-draw-ant {
  to {
    stroke-dashoffset: 0;
  }
}

@keyframes zeus-loader-strike-draw-bolt {
  to {
    stroke-dashoffset: 0;
  }
}

@keyframes zeus-loader-strike-impact-burst {
  0%,
  38% {
    opacity: 0;
    transform: translate(-50%, 38%) scale(0.05);
    filter: brightness(1);
  }

  45% {
    opacity: 1;
    transform: translate(-50%, 38%) scale(1);
    filter: brightness(1.85);
  }

  62% {
    opacity: 0.55;
    transform: translate(-50%, 36%) scale(1.08);
    filter: brightness(1.2);
  }

  100% {
    opacity: 0;
    transform: translate(-50%, 32%) scale(1.32);
    filter: brightness(0.92);
  }
}

@keyframes zeus-loader-strike-bolt-release {
  0%,
  62% {
    opacity: 1;
    transform: translateY(0) scaleY(1);
    filter: brightness(1);
  }

  66% {
    filter: brightness(1.85);
    transform: translateY(-0.02) scaleY(1.015);
  }

  76% {
    opacity: 0.94;
    filter: brightness(1.08);
    transform: translateY(-2%) scaleY(1.025);
  }

  100% {
    opacity: 0;
    filter: brightness(0.92);
    transform: translateY(-14%) scaleY(1.06);
  }
}

/* ——— scan ——— */
.zeus-loader__scan-dim {
  position: absolute;
  inset: 0;
  z-index: 0;
  border-radius: inherit;
  background: color-mix(in oklch, var(--foreground) 9%, transparent);
}

.zeus-loader__scan-halo {
  position: absolute;
  inset: -6px;
  z-index: 1;
  border-radius: inherit;
  background: radial-gradient(
    circle at 50% 42%,
    color-mix(in oklab, var(--color-brand-primary) 46%, white) 0%,
    color-mix(in oklab, var(--color-brand-secondary) 28%, transparent) 48%,
    transparent 70%
  );
  opacity: 0.5;
  animation: zeus-loader-scan-halo-pulse 1.3s ease-in-out infinite;
  animation-delay: var(--zeus-scan-delay, 0s);
}

.zeus-loader__scan-band {
  position: absolute;
  inset: 0;
  z-index: 2;
  overflow: hidden;
  border-radius: inherit;
}

.zeus-loader__scan-band::before {
  content: "";
  position: absolute;
  left: -28%;
  right: -28%;
  top: -50%;
  height: 44%;
  border-radius: 3px;
  background: linear-gradient(
    180deg,
    transparent 0%,
    color-mix(in oklab, var(--color-brand-primary) 38%, transparent) 14%,
    color-mix(in oklab, white 55%, var(--color-brand-secondary) 45%) 38%,
    #ffffff 50%,
    color-mix(in oklab, var(--color-brand-primary) 42%, white) 58%,
    color-mix(in oklab, var(--color-brand-secondary) 62%, white) 76%,
    transparent 100%
  );
  filter: blur(0.75px);
  mix-blend-mode: screen;
  opacity: 0.96;
  transform: translateY(-55%) skewX(-8deg);
  animation: zeus-loader-scan-wave-vertical 1.16s cubic-bezier(0.42, 0, 0.58, 1) infinite;
  animation-delay: var(--zeus-scan-delay, 0s);
}

.zeus-loader__scan-band::after {
  content: "";
  position: absolute;
  inset: -8%;
  background: linear-gradient(
    110deg,
    transparent 0%,
    transparent 38%,
    color-mix(in oklab, white 86%, var(--color-brand-primary) 14%) 50%,
    transparent 62%,
    transparent 100%
  );
  mix-blend-mode: screen;
  opacity: 0;
  transform: translateX(-135%) skewX(-12deg);
  animation: zeus-loader-scan-wave-sweep 1.82s ease-in-out infinite;
  animation-delay: calc(var(--zeus-scan-delay, 0s) + 0.22s);
}

.zeus-loader__scan-spark {
  position: absolute;
  z-index: 3;
  width: 7px;
  height: 10px;
  clip-path: polygon(38% 0%, 82% 0%, 57% 35%, 100% 35%, 30% 100%, 46% 58%, 8% 58%);
  box-shadow: 0 0 8px var(--cg-spark-color, #ffcc00);
  background: var(--cg-spark-color);
  opacity: 0;
  pointer-events: none;
}

.zeus-loader__scan-spark-a {
  --cg-spark-color: #ffcc00;
  top: 2px;
  right: 0;
  transform-origin: 40% 55%;
  animation: zeus-loader-spark-a 1.05s steps(5, end) infinite;
  animation-delay: calc(var(--zeus-scan-delay, 0s));
}

.zeus-loader__scan-spark-b {
  --cg-spark-color: #ff453a;
  bottom: 2px;
  left: -1px;
  transform-origin: 52% 48%;
  animation: zeus-loader-spark-b 1.05s steps(5, end) infinite;
  animation-delay: calc(var(--zeus-scan-delay, 0s) + 0.08s);
}

.zeus-loader__scan-spark-c {
  --cg-spark-color: #ff453a;
  top: 0;
  left: 4px;
  transform-origin: 50% 50%;
  animation: zeus-loader-spark-c 1.05s steps(5, end) infinite;
  animation-delay: calc(var(--zeus-scan-delay, 0s) + 0.04s);
}

.zeus-loader__scan-spark-d {
  --cg-spark-color: #ffcc00;
  bottom: 0;
  right: 2px;
  transform-origin: 50% 50%;
  animation: zeus-loader-spark-d 1.05s steps(5, end) infinite;
  animation-delay: calc(var(--zeus-scan-delay, 0s) + 0.12s);
}

@keyframes zeus-loader-scan-halo-pulse {
  0%,
  100% {
    opacity: 0.36;
    transform: scale(0.9);
  }

  40% {
    opacity: 0.68;
    transform: scale(1.06);
  }

  65% {
    opacity: 0.44;
    transform: scale(0.96);
  }
}

@keyframes zeus-loader-scan-wave-vertical {
  0% {
    transform: translateY(-65%) skewX(-8deg);
    opacity: 0;
  }

  9% {
    opacity: 1;
  }

  91% {
    opacity: 1;
  }

  100% {
    transform: translateY(265%) skewX(-8deg);
    opacity: 0;
  }
}

@keyframes zeus-loader-scan-wave-sweep {
  0%,
  22%,
  100% {
    opacity: 0;
    transform: translateX(-135%) skewX(-12deg);
  }

  30% {
    opacity: 0.88;
    transform: translateX(-42%) skewX(-12deg);
  }

  40% {
    opacity: 0.62;
    transform: translateX(32%) skewX(-12deg);
  }

  50% {
    opacity: 0;
    transform: translateX(125%) skewX(-12deg);
  }
}

@keyframes zeus-loader-spark-a {
  0%,
  100% {
    opacity: 0;
    transform: translate(0, 0) rotate(8deg) scale(0.6);
  }

  8% {
    opacity: 1;
    transform: translate(1px, -1px) rotate(16deg) scale(1);
  }

  14% {
    opacity: 0;
    transform: translate(4px, -3px) rotate(22deg) scale(0.8);
  }

  24% {
    opacity: 0.95;
    transform: translate(2px, -2px) rotate(14deg) scale(0.96);
  }

  31% {
    opacity: 0;
    transform: translate(6px, -4px) rotate(23deg) scale(0.7);
  }
}

@keyframes zeus-loader-spark-b {
  0%,
  100% {
    opacity: 0;
    transform: translate(0, 0) rotate(-12deg) scale(0.6);
  }

  42% {
    opacity: 0;
    transform: translate(0, 0) rotate(-12deg) scale(0.6);
  }

  52% {
    opacity: 1;
    transform: translate(-1px, 1px) rotate(-22deg) scale(1);
  }

  58% {
    opacity: 0;
    transform: translate(-4px, 3px) rotate(-26deg) scale(0.8);
  }

  68% {
    opacity: 0.9;
    transform: translate(-2px, 2px) rotate(-20deg) scale(0.94);
  }

  75% {
    opacity: 0;
    transform: translate(-6px, 5px) rotate(-28deg) scale(0.72);
  }
}

@keyframes zeus-loader-spark-c {
  0%,
  100% {
    opacity: 0;
    transform: translate(0, 0) rotate(2deg) scale(0.58);
  }

  30% {
    opacity: 0;
    transform: translate(0, 0) rotate(2deg) scale(0.58);
  }

  36% {
    opacity: 0.95;
    transform: translate(1px, -2px) rotate(10deg) scale(0.98);
  }

  43% {
    opacity: 0;
    transform: translate(3px, -5px) rotate(16deg) scale(0.72);
  }
}

@keyframes zeus-loader-spark-d {
  0%,
  100% {
    opacity: 0;
    transform: translate(0, 0) rotate(-4deg) scale(0.58);
  }

  60% {
    opacity: 0;
    transform: translate(0, 0) rotate(-4deg) scale(0.58);
  }

  67% {
    opacity: 0.95;
    transform: translate(1px, 2px) rotate(-11deg) scale(0.98);
  }

  74% {
    opacity: 0;
    transform: translate(3px, 5px) rotate(-18deg) scale(0.72);
  }
}

@media (prefers-reduced-motion: reduce) {
  .zeus-loader__strike-svg,
  .zeus-loader__strike-flicker-bar,
  .zeus-loader__strike-chain,
  .zeus-loader__strike-ant,
  .zeus-loader__strike-main,
  .zeus-loader__strike-bolt {
    animation: none !important;
  }

  .zeus-loader__strike-ant,
  .zeus-loader__strike-main,
  .zeus-loader__strike-chain {
    stroke-dashoffset: 0 !important;
  }

  .zeus-loader__strike-svg {
    opacity: 0;
  }

  .zeus-loader--strike-phase {
    opacity: 0;
    animation: zeus-loader-strike-rm 200ms ease-out forwards !important;
  }

  .zeus-loader__strike-backdrop {
    opacity: 0.65;
    background: color-mix(in oklab, var(--color-brand-primary) 32%, transparent);
  }

  .zeus-loader__strike-impact {
    opacity: 0;
    transform: translate(-50%, 38%);
    animation: zeus-loader-strike-rm-flash 210ms ease-out forwards !important;
  }

  .zeus-loader__scan-halo,
  .zeus-loader__scan-band::before,
  .zeus-loader__scan-band::after,
  .zeus-loader__scan-spark {
    animation: none !important;
  }

  .zeus-loader__scan-band::before {
    top: 12%;
    left: -10%;
    right: -10%;
    height: 76%;
    opacity: 0.16;
    transform: none;
    filter: none;
  }

  .zeus-loader__scan-band::after {
    opacity: 0;
  }

  .zeus-loader__scan-halo {
    opacity: 0.34;
    transform: none;
  }

  .zeus-loader__scan-spark {
    opacity: 0;
  }

  .zeus-loader__scan-dim {
    opacity: 0.92;
  }
}

@keyframes zeus-loader-strike-rm {
  0% {
    opacity: 0;
  }

  28% {
    opacity: 0.85;
  }

  100% {
    opacity: 0;
  }
}

@keyframes zeus-loader-strike-rm-flash {
  0%,
  100% {
    opacity: 0;
    transform: translate(-50%, 38%);
  }

  35% {
    opacity: 0.45;
    transform: translate(-50%, 38%);
  }
}
</style>
